import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { groupSuppliers, SupplierGroupingSummary } from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload an AP Reconciliation Excel file (.xlsx) with an "AP Reconciliation" sheet.',
  'Optionally set an output filename (defaults to <your file>_by_supplier.xlsx).',
  'Click Group by Supplier. The grouped workbook downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function SupplierGroupingPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [outputFilename, setOutputFilename] = useState('');
  const { status, error, result, fail, run } = useFileRun<FileResult<SupplierGroupingSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload an AP Reconciliation file first.');
      return;
    }
    await run(() => groupSuppliers(files[0], outputFilename), 'Grouping failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 What you get</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Each supplier's invoices grouped under a bold supplier header, oldest first.</li>
          <li>A "Total &lt;Supplier&gt;" row with live SUM formulas after each group.</li>
          <li>A grand Total row that sums each supplier's own total (no double-counting).</li>
          <li>The Remarks and Comments columns are dropped.</li>
        </ul>
      </div>

      <FileDrop
        label="AP Reconciliation file"
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="sg-output" className="mb-1.5">
          Output filename (optional)
        </Label>
        <Input
          id="sg-output"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="<your file>_by_supplier.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Grouping…' : 'Group by Supplier'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Grouping…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {result.summary && (
            <p className="mt-1 text-xs text-green-800">
              Grouped {result.summary.suppliers} supplier(s), {result.summary.rows} rows total.
            </p>
          )}
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload a .xlsx file above to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default SupplierGroupingPanel;
